import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { Observable, lastValueFrom } from 'rxjs';

interface YourDriveLoginResponse {
  id: string;
  userName: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  isAuthenticated: boolean;
  profilePhoto: string;
  isAdmin: boolean;
  claims: Array<{ claimType: string; claimValue: string }>;
  bearerToken: string;
}

/**
 * Service for YourDrive .NET API integration
 * Connects AccessLife Admin with YourDrive backend at localhost:5000
 */
@Injectable({
  providedIn: 'root'
})
export class YourDriveService {
  private readonly YOURDRIVE_API_URL = 'https://driveapi.accesslife.org/api';
  private readonly YOURDRIVE_FRONTEND_URL = 'https://drive.accesslife.org';

  private yourDriveToken: string | null = null;

  constructor(
    private http: HttpClient,
    private afAuth: AngularFireAuth
  ) {
    // Check if token exists in localStorage on initialization
    this.yourDriveToken = localStorage.getItem('yourdrive_auth_token');
  }

  /**
   * Test backend connectivity (for debugging)
   * Makes a simple request to see what the backend returns
   */
  async testBackendConnection(): Promise<any> {
    try {
      console.log('🧪 Testing YourDrive backend connection...');
      console.log('URL:', `${this.YOURDRIVE_API_URL}/User/firebase-login`);

      const user = await this.afAuth.currentUser;
      if (!user) {
        console.error('❌ No Firebase user logged in');
        return { error: 'No Firebase user' };
      }

      const firebaseToken = await user.getIdToken();
      console.log('✅ Firebase token obtained (length):', firebaseToken.length);

      const response = await lastValueFrom(
        this.http.post(
          `${this.YOURDRIVE_API_URL}/User/firebase-login`,
          {
            firebaseToken: firebaseToken,
            latitude: '19.0760',
            longitude: '72.8777',
            device: 'Test Client'
          },
          { observe: 'response' } // Get full response including headers
        )
      );

      console.log('📦 Full Response:', response);
      console.log('📦 Status:', response.status);
      console.log('📦 Headers:', response.headers);
      console.log('📦 Body:', response.body);

      return response.body;
    } catch (error: any) {
      console.error('❌ Backend test failed:', error);
      console.error('Status:', error.status);
      console.error('Error body:', error.error);
      return { error: error.message, details: error.error };
    }
  }

  /**
   * Login to YourDrive using Firebase token
   * Calls the /api/User/firebase-login endpoint on YourDrive backend
   */
  async loginToYourDrive(): Promise<YourDriveLoginResponse> {
    try {
      const user = await this.afAuth.currentUser;
      if (!user) {
        throw new Error('No Firebase user logged in');
      }

      const firebaseToken = await user.getIdToken();
      const device = this.getDeviceInfo();
      const location = await this.getCurrentLocation();

      console.log('🔑 Logging into YourDrive with Firebase token...');
      console.log('API URL:', `${this.YOURDRIVE_API_URL}/User/firebase-login`);
      console.log('Device:', device);

      const response = await lastValueFrom(
        this.http.post<YourDriveLoginResponse>(
          `${this.YOURDRIVE_API_URL}/User/firebase-login`,
          {
            firebaseToken: firebaseToken,
            latitude: location.latitude,
            longitude: location.longitude,
            device: device
          }
        )
      );

      console.log('📦 Response received:', response);
      console.log('📦 Response type:', typeof response);
      console.log('📦 Response keys:', response ? Object.keys(response) : 'null');

      // Check if response is an error array (e.g., ['Object reference not set...'])
      if (Array.isArray(response)) {
        const errorMsg = response[0] || 'Unknown backend error';
        console.error('❌ Backend returned error array:', response);
        throw new Error(`YourDrive backend error: ${errorMsg}`);
      }

      // Check if response has bearerToken
      if (response && response.bearerToken) {
        this.yourDriveToken = response.bearerToken;

        // Store in localStorage with unique keys
        localStorage.setItem('yourdrive_auth_token', response.bearerToken);
        localStorage.setItem('yourdrive_user_email', response.email);
        localStorage.setItem('yourdrive_user_name', `${response.firstName} ${response.lastName}`);
        localStorage.setItem('yourdrive_user_id', response.id);

        console.log('✅ Successfully logged into YourDrive');
        console.log('User:', response.userName);
        console.log('Email:', response.email);
        console.log('Is Admin:', response.isAdmin);

        return response;
      }

      console.error('❌ Invalid response structure. Expected bearerToken but got:', response);
      throw new Error('Invalid response from YourDrive API - missing bearerToken');
    } catch (error: any) {
      console.error('❌ YourDrive login failed:', error);
      console.error('Error status:', error.status);
      console.error('Error message:', error.message);
      console.error('Error response:', error.error);

      // Handle specific error cases
      if (error.status === 0) {
        throw new Error('Cannot connect to YourDrive backend. Is it running on http://localhost:5000?');
      } else if (error.status === 400) {
        const errorMsg = error.error?.errors ? JSON.stringify(error.error.errors) : 'Bad request';
        throw new Error(`YourDrive API error (400): ${errorMsg}`);
      } else if (error.status === 401) {
        throw new Error('Firebase authentication failed. Please login to AccessLife first.');
      } else if (error.status === 404) {
        throw new Error('YourDrive API endpoint not found. Check backend configuration.');
      } else if (error.message?.includes('bearerToken')) {
        throw new Error('Response received but missing authentication token. Check YourDrive backend response format.');
      }

      throw error;
    }
  }

  /**
   * Open YourDrive in a new window/tab
   * YourDrive frontend will read the token from localStorage
   */
  openYourDrive(): void {
    const token = this.getYourDriveToken();
    if (!token) {
      throw new Error('Not logged into YourDrive. Please login first.');
    }

    console.log('🚀 Opening YourDrive in new window...');

    // Open YourDrive frontend (which also runs on port 4200)
    // Note: In production, YourDrive would be on a different port/domain
    const width = 1200;
    const height = 800;
    const left = (screen.width - width) / 2;
    const top = (screen.height - height) / 2;

    const yourdriveWindow = window.open(
      this.YOURDRIVE_FRONTEND_URL,
      'YourDrive',
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`
    );

    if (!yourdriveWindow) {
      throw new Error('Failed to open YourDrive window. Please check popup blockers.');
    }
  }

  /**
   * Get stored YourDrive token
   */
  getYourDriveToken(): string | null {
    if (!this.yourDriveToken) {
      this.yourDriveToken = localStorage.getItem('yourdrive_auth_token');
    }
    return this.yourDriveToken;
  }

  /**
   * Check if user is logged into YourDrive
   */
  isLoggedInToYourDrive(): boolean {
    return !!this.getYourDriveToken();
  }

  /**
   * Get user info from localStorage
   */
  getUserInfo(): { email: string; name: string; id: string } | null {
    const email = localStorage.getItem('yourdrive_user_email');
    const name = localStorage.getItem('yourdrive_user_name');
    const id = localStorage.getItem('yourdrive_user_id');

    if (email && name && id) {
      return { email, name, id };
    }

    return null;
  }

  /**
   * Logout from YourDrive - clear all stored data
   */
  logout(): void {
    this.yourDriveToken = null;
    localStorage.removeItem('yourdrive_auth_token');
    localStorage.removeItem('yourdrive_user_email');
    localStorage.removeItem('yourdrive_user_name');
    localStorage.removeItem('yourdrive_user_id');
    console.log('🚪 Logged out from YourDrive');
  }

  /**
   * Get user documents from YourDrive
   * Example API call using the JWT token
   */
  getUserDocuments(): Observable<any> {
    const token = this.getYourDriveToken();
    if (!token) {
      throw new Error('Not logged into YourDrive');
    }

    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`
    });

    return this.http.get(`${this.YOURDRIVE_API_URL}/Document`, { headers });
  }

  /**
   * Upload file to YourDrive
   * Example file upload using the JWT token
   */
  uploadFile(file: File, folderId?: string): Observable<any> {
    const token = this.getYourDriveToken();
    if (!token) {
      throw new Error('Not logged into YourDrive');
    }

    const formData = new FormData();
    formData.append('file', file);
    if (folderId) {
      formData.append('folderId', folderId);
    }

    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`
    });

    return this.http.post(`${this.YOURDRIVE_API_URL}/Document/upload`, formData, { headers });
  }

  /**
   * Get device information for tracking
   */
  private getDeviceInfo(): string {
    const userAgent = navigator.userAgent;
    if (/android/i.test(userAgent)) {
      return 'Android - AccessLife Admin';
    } else if (/iPad|iPhone|iPod/.test(userAgent)) {
      return 'iOS - AccessLife Admin';
    } else if (/Mac/.test(userAgent)) {
      return 'macOS - AccessLife Admin';
    } else if (/Win/.test(userAgent)) {
      return 'Windows - AccessLife Admin';
    }
    return 'Web Browser - AccessLife Admin';
  }

  /**
   * Get current location (optional)
   * Returns 0,0 if geolocation is not available
   */
  private async getCurrentLocation(): Promise<{ latitude: string; longitude: string }> {
    return new Promise((resolve) => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            resolve({
              latitude: position.coords.latitude.toString(),
              longitude: position.coords.longitude.toString()
            });
          },
          () => {
            // Default to Mumbai coordinates if geolocation fails
            resolve({ latitude: '19.0760', longitude: '72.8777' });
          },
          { timeout: 5000 }
        );
      } else {
        resolve({ latitude: '19.0760', longitude: '72.8777' });
      }
    });
  }
}

